const EFFECTIVE = 'April 9, 2026';

function LegalShell({ eyebrow, title, subtitle, sections, onNavigate }) {
  return (
    <main className="page legal">
      <nav className="nav">
        <button className="nav-back" onClick={() => onNavigate('home')}>
          <div className="logo-mark" aria-hidden="true">
            <span className="logo-dot" />
          </div>
          <span className="logo-word mono">APPSHELL</span>
        </button>
        <button className="nav-contact back-link" onClick={() => onNavigate('home')}>
          ← Back
        </button>
      </nav>

      <header className="legal-head">
        <div className="legal-eyebrow-wrap">
          <span className="legal-eyebrow-dot" />
          <p className="legal-eyebrow mono">{eyebrow}</p>
        </div>
        <h1 className="legal-title">{title}</h1>
        <p className="legal-sub">{subtitle}</p>
        <div className="legal-meta mono">
          <span>Effective {EFFECTIVE}</span>
          <span className="legal-meta-dot">·</span>
          <span>APPSHELL LLC · Wyoming, USA</span>
        </div>
      </header>

      <article className="legal-body">
        {sections.map((s, i) => (
          <section className="legal-section" key={s.h}>
            <div className="legal-section-head">
              <span className="legal-idx mono">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h2>{s.h}</h2>
            </div>
            {s.p.map((para, j) => (
              <p key={j}>{para}</p>
            ))}
            {s.list && (
              <ul>
                {s.list.map((li) => (
                  <li key={li}>{li}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </article>

      <footer className="footer">
        <span className="footer-brand">© 2026 APPSHELL LLC</span>
        <div className="footer-links">
          <button className="footer-link" onClick={() => onNavigate('home')}>Home</button>
          <span className="footer-dot">·</span>
          <button className="footer-link" onClick={() => onNavigate('privacy')}>Privacy</button>
          <span className="footer-dot">·</span>
          <button className="footer-link" onClick={() => onNavigate('terms')}>Terms</button>
          <span className="footer-dot">·</span>
          <button className="footer-link" onClick={() => onNavigate('content')}>Content Policy</button>
        </div>
      </footer>
    </main>
  );
}

export function Privacy({ onNavigate }) {
  const sections = [
    {
      h: 'Introduction',
      p: [
        'APPSHELL LLC ("APPSHELL," "we," "us," or "our") is a limited liability company organized under the laws of the State of Wyoming, United States, with its principal office located at 30 N Gould St #59828, Sheridan, WY 82801.',
        'This Privacy Policy ("Policy") describes how APPSHELL collects, uses, discloses, and safeguards personal information in connection with our websites, mobile applications, and related services (collectively, the "Services"). By accessing or using the Services, you acknowledge that you have read and understood the practices described in this Policy.',
      ],
    },
    {
      h: 'Scope',
      p: [
        'This Policy applies to personal information processed by APPSHELL in its capacity as a data controller. It does not apply to personal information processed by third parties whose products or services are not operated by APPSHELL, even if they are linked to or otherwise integrated with the Services.',
      ],
    },
    {
      h: 'Information we collect',
      p: [
        'We collect personal information in the following categories:',
      ],
      list: [
        'Information you provide directly, including your name, email address, and any content you submit when contacting us, requesting support, or participating in our Services.',
        'Information collected automatically, including Internet Protocol (IP) address, device identifiers, operating system and browser type, language preferences, referring URLs, pages viewed, and timestamps of access.',
        'Information derived from cookies, local storage, and similar technologies used to operate, secure, and measure the performance of the Services.',
        'Information received from service providers acting on our behalf for purposes such as hosting, analytics, security, and communications.',
      ],
    },
    {
      h: 'Purposes of processing',
      p: [
        'APPSHELL processes personal information for the following purposes:',
      ],
      list: [
        'to provide, operate, maintain, and improve the Services;',
        'to respond to inquiries, fulfill requests, and deliver customer support;',
        'to monitor usage and performance of the Services and to diagnose technical issues;',
        'to protect the security and integrity of the Services and to prevent, detect, and investigate fraudulent or unlawful activity;',
        'to comply with applicable legal obligations and to establish, exercise, or defend legal claims.',
      ],
    },
    {
      h: 'Legal bases',
      p: [
        'Where required by applicable law, APPSHELL processes personal information on one or more of the following legal bases: (i) the performance of a contract to which you are a party; (ii) compliance with a legal obligation; (iii) your consent, where consent has been requested and provided; and (iv) our legitimate interests in operating, securing, and improving the Services, provided that such interests are not overridden by your rights and freedoms.',
      ],
    },
    {
      h: 'Disclosure of information',
      p: [
        'APPSHELL does not sell personal information, and we do not share personal information for cross-context behavioral advertising purposes. We may disclose personal information only in the following limited circumstances:',
      ],
      list: [
        'to service providers acting under written contracts that restrict their use of the information to purposes specified by APPSHELL;',
        'to comply with applicable law, legal process, or enforceable governmental requests;',
        'to enforce our Terms of Use or protect the rights, property, or safety of APPSHELL, our users, or the public; and',
        'in connection with, or during negotiations of, a merger, acquisition, financing, or sale of all or part of our business, subject to appropriate confidentiality safeguards.',
      ],
    },
    {
      h: 'Cookies and similar technologies',
      p: [
        'The Services may use cookies and similar technologies strictly necessary to operate core functionality, remember preferences, and generate aggregate usage statistics. You may disable cookies through your browser settings; however, doing so may affect the availability or performance of certain features.',
      ],
    },
    {
      h: 'International transfers',
      p: [
        'APPSHELL is located in the United States, and personal information we collect may be processed in the United States or in other jurisdictions where our service providers operate. Where required by applicable law, we implement appropriate safeguards to ensure that international transfers are carried out in accordance with applicable data protection requirements.',
      ],
    },
    {
      h: 'Data retention',
      p: [
        'APPSHELL retains personal information only for as long as is reasonably necessary to fulfill the purposes for which it was collected, including for the purposes of satisfying legal, accounting, or reporting requirements, or to resolve disputes and enforce agreements. Upon expiration of the applicable retention period, personal information is deleted or anonymized.',
      ],
    },
    {
      h: 'Your rights',
      p: [
        'Subject to applicable law, you may have the right to request access to, correction of, deletion of, or a portable copy of the personal information we hold about you, as well as the right to object to or restrict certain processing. To exercise any of these rights, please contact us at hello@appshell.co. We will respond to verifiable requests within the timeframes required by applicable law.',
      ],
    },
    {
      h: 'Security',
      p: [
        'APPSHELL maintains administrative, technical, and physical safeguards designed to protect personal information against unauthorized access, disclosure, alteration, and destruction. No method of transmission over the Internet or method of electronic storage is completely secure, and we cannot guarantee absolute security.',
      ],
    },
    {
      h: "Children's privacy",
      p: [
        'The Services are not directed to, and APPSHELL does not knowingly collect personal information from, children under the age of 13 (or the equivalent minimum age in the relevant jurisdiction). If we become aware that we have collected personal information from such a child without the required parental consent, we will take reasonable steps to delete it promptly.',
      ],
    },
    {
      h: 'Changes to this Policy',
      p: [
        'APPSHELL may update this Policy from time to time to reflect changes in our practices, technologies, or legal requirements. The "Effective" date at the top of this Policy indicates when it was last revised. Material changes will be communicated through the Services or by other reasonable means prior to taking effect.',
      ],
    },
    {
      h: 'Contact',
      p: [
        'Questions, comments, or requests regarding this Policy may be directed to:',
        'APPSHELL LLC · 30 N Gould St #59828 · Sheridan, WY 82801, United States · hello@appshell.co',
      ],
    },
  ];

  return (
    <LegalShell
      eyebrow="Privacy"
      title="Privacy Policy"
      subtitle="This Policy explains how APPSHELL LLC collects, uses, and safeguards personal information in connection with our Services."
      sections={sections}
      onNavigate={onNavigate}
    />
  );
}

export function Terms({ onNavigate }) {
  const sections = [
    {
      h: 'Acceptance of Terms',
      p: [
        'These Terms of Use ("Terms") constitute a binding agreement between you ("you" or "User") and APPSHELL LLC, a Wyoming limited liability company with its principal office at 30 N Gould St #59828, Sheridan, WY 82801 ("APPSHELL," "we," "us," or "our"), governing your access to and use of our websites, mobile applications, and related services (collectively, the "Services").',
        'By accessing or using the Services, you represent that you have read, understood, and agreed to be bound by these Terms and by our Privacy Policy. If you do not agree to these Terms, you must not access or use the Services.',
      ],
    },
    {
      h: 'Eligibility',
      p: [
        'You represent and warrant that you are at least the age of majority in your jurisdiction of residence, or, if you are a minor, that you have obtained the consent of your parent or legal guardian, and that you have the legal capacity to enter into a binding agreement. If you access the Services on behalf of a legal entity, you represent and warrant that you are authorized to bind such entity to these Terms.',
      ],
    },
    {
      h: 'The Services',
      p: [
        'The Services are provided for informational and commercial purposes and enable Users to learn about APPSHELL, its products, and its offerings, and to contact us. Nothing contained in the Services shall be construed as an offer, commitment, quotation, or engagement on the part of APPSHELL; any commercial relationship between APPSHELL and a User shall be governed exclusively by a separate written agreement executed by both parties.',
        'APPSHELL reserves the right, at any time and without prior notice, to modify, suspend, or discontinue any portion of the Services, in whole or in part.',
      ],
    },
    {
      h: 'Acceptable use',
      p: [
        'You agree to use the Services only for lawful purposes and in accordance with these Terms. You shall not, and shall not permit any third party to:',
      ],
      list: [
        'access or use the Services in violation of any applicable law, regulation, or contractual or fiduciary obligation;',
        'attempt to gain unauthorized access to the Services, related systems, or the accounts of other Users, or interfere with the security or integrity of the Services;',
        'probe, scan, or test the vulnerability of the Services, or circumvent any security or authentication measure;',
        'upload, transmit, or distribute any virus, worm, malware, or other malicious code, or engage in any activity that imposes an unreasonable or disproportionately large load on our infrastructure;',
        'use any robot, spider, scraper, or other automated means to access, collect, or copy any content or data from the Services, except as permitted by our robots.txt file or with our prior written consent; or',
        'use the Services to infringe, misappropriate, or violate any intellectual property, privacy, publicity, contractual, or other right of APPSHELL or any third party.',
      ],
    },
    {
      h: 'Intellectual property',
      p: [
        'The Services, including all text, graphics, logos, icons, images, audio and video content, software, source code, and the selection and arrangement thereof (the "APPSHELL Materials"), are the exclusive property of APPSHELL or its licensors and are protected by copyright, trademark, and other intellectual property laws of the United States and other jurisdictions.',
        'Subject to your compliance with these Terms, APPSHELL grants you a limited, non-exclusive, non-transferable, non-sublicensable, and revocable license to access and use the Services for your personal, non-commercial use. All rights not expressly granted are reserved by APPSHELL.',
      ],
    },
    {
      h: 'User submissions and feedback',
      p: [
        'If you submit any comments, suggestions, ideas, or other materials to APPSHELL regarding the Services ("Feedback"), you hereby grant APPSHELL a perpetual, irrevocable, worldwide, royalty-free, sublicensable, and transferable license to use, reproduce, modify, publish, and otherwise exploit such Feedback for any purpose, without any obligation or compensation to you.',
      ],
    },
    {
      h: 'Third-party materials',
      p: [
        'The Services may contain links to, or integrations with, third-party websites, applications, or services that are not owned or controlled by APPSHELL. APPSHELL does not endorse and is not responsible for the content, policies, or practices of any third party. Your access to and use of any third-party resource is at your own risk and is governed solely by the terms and policies of the applicable third party.',
      ],
    },
    {
      h: 'Disclaimers',
      p: [
        'THE SERVICES AND ALL CONTENT CONTAINED THEREIN ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WITHOUT LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.',
        'APPSHELL DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, SECURE, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS, OR THAT ANY DEFECTS WILL BE CORRECTED. NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED FROM APPSHELL OR THROUGH THE SERVICES, SHALL CREATE ANY WARRANTY NOT EXPRESSLY STATED HEREIN.',
      ],
    },
    {
      h: 'Limitation of liability',
      p: [
        'TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL APPSHELL OR ITS MEMBERS, MANAGERS, OFFICERS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUES, DATA, GOODWILL, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR IN CONNECTION WITH YOUR ACCESS TO OR USE OF, OR INABILITY TO ACCESS OR USE, THE SERVICES, WHETHER BASED IN CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT APPSHELL HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.',
        'IN NO EVENT SHALL THE TOTAL AGGREGATE LIABILITY OF APPSHELL ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICES EXCEED ONE HUNDRED U.S. DOLLARS (USD $100.00).',
      ],
    },
    {
      h: 'Indemnification',
      p: [
        'You agree to defend, indemnify, and hold harmless APPSHELL and its members, managers, officers, employees, agents, and licensors from and against any and all claims, damages, liabilities, losses, costs, and expenses (including reasonable attorneys\' fees) arising out of or relating to: (i) your access to or use of the Services; (ii) your breach of these Terms; (iii) your violation of any applicable law or the rights of any third party; or (iv) any content or information you submit to or through the Services.',
      ],
    },
    {
      h: 'Termination',
      p: [
        'APPSHELL may, in its sole discretion and without notice or liability, suspend or terminate your access to the Services at any time, for any reason or for no reason, including without limitation if APPSHELL believes that you have violated these Terms. Upon termination, all provisions of these Terms that by their nature should survive termination shall survive, including without limitation ownership provisions, warranty disclaimers, indemnity obligations, and limitations of liability.',
      ],
    },
    {
      h: 'Governing law and jurisdiction',
      p: [
        'These Terms and any dispute arising out of or relating to these Terms or the Services shall be governed by and construed in accordance with the laws of the State of Wyoming, United States, without regard to its conflict-of-laws principles.',
        'You and APPSHELL irrevocably consent to the exclusive jurisdiction and venue of the state and federal courts located in Sheridan County, Wyoming, for the resolution of any such dispute, and waive any objection to the laying of venue of, or to the convenience of the forum of, any such action or proceeding.',
      ],
    },
    {
      h: 'Changes to these Terms',
      p: [
        'APPSHELL reserves the right, in its sole discretion, to modify these Terms at any time. When we make material changes, we will update the "Effective" date above and, where appropriate, provide additional notice through the Services. Your continued access to or use of the Services following the effective date of any revised Terms constitutes your acceptance of such revised Terms.',
      ],
    },
    {
      h: 'Miscellaneous',
      p: [
        'These Terms, together with the Privacy Policy and any other agreements expressly incorporated herein by reference, constitute the entire agreement between you and APPSHELL concerning the Services and supersede all prior or contemporaneous communications and proposals. If any provision of these Terms is held to be invalid or unenforceable, such provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect. The failure of APPSHELL to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. You may not assign or transfer these Terms without APPSHELL\'s prior written consent; APPSHELL may assign these Terms at any time without notice.',
      ],
    },
    {
      h: 'Contact',
      p: [
        'Questions or notices concerning these Terms may be directed to:',
        'APPSHELL LLC · 30 N Gould St #59828 · Sheridan, WY 82801, United States · hello@appshell.co',
      ],
    },
  ];

  return (
    <LegalShell
      eyebrow="Terms"
      title="Terms of Use"
      subtitle="These Terms govern your access to and use of the websites, applications, and services provided by APPSHELL LLC."
      sections={sections}
      onNavigate={onNavigate}
    />
  );
}

export function ContentPolicy({ onNavigate }) {
  const sections = [
    {
      h: 'How radiofm works',
      p: [
        'radiofm is a directory client. It does not host, store, record, download, re-encode, proxy or re-broadcast any audio. Station names and stream URLs are supplied by the Radio Browser community directory (https://api.radio-browser.info), a publicly available database maintained by its own users. When someone plays a station in radiofm, their device connects directly to the stream URL that the station itself publishes for public listening — technically the same act as opening that URL in a web browser.',
        'radiofm displays no broadcaster logos, marks or artwork.',
        'Listening is free. Our optional subscription unlocks features of the app itself — a sleep timer and an unlimited favourites list — and never access to any station.',
      ],
    },
    {
      h: 'Takedown requests',
      p: [
        "If you are a rights holder and do not want your station listed, write to hello@appshell.co with the station name and its stream URL. We remove the listing on request. We ask only that the request come from a contact at the station's own domain; we require no further proof of ownership. We act on such requests within one business day.",
      ],
    },
    {
      h: 'Contact',
      p: [
        'hello@appshell.co',
      ],
    },
  ];

  return (
    <LegalShell
      eyebrow="Content"
      title="Content & Takedown Policy"
      subtitle="This policy applies to radiofm, published by Appshell."
      sections={sections}
      onNavigate={onNavigate}
    />
  );
}
